import { BrandBadge } from "@/components/BrandMark";
import {
  CalendarDays, Home, Users, ClipboardCheck, AlertTriangle, Wrench, MessageSquare,
  Receipt, CreditCard, Zap, Check, ArrowRight, MapPin, Navigation,
} from "lucide-react";

const SHOT = "/tour/shots";
// Small copies for the panes: six full-size screenshots decoding at once was costing the film
// half its frame rate.
const THUMB = "/tour/shots/thumb";
const SHOT_V = "14";   // bump when the screenshots are recaptured so browsers refetch them
const PHOTO = "/tour/photos";
const PHOTO_V = "1";   // bump when the photography is replaced

/** A cinematic move across real licensed photography: the physical side of the operation. */
export const Photo = ({ name, move = "in", dim = 0.34, focus = "center", vignette = true,
                        blur = 0 }) => (
  <div className="absolute inset-0 overflow-hidden">
    <img src={`${PHOTO}/${name}.jpg?v=${PHOTO_V}`} alt="" aria-hidden decoding="async"
         className={`h-full w-full tour-shot tour-ken tour-ken-${move}`}
         style={{ objectPosition: focus,
                  filter: `saturate(0.92) contrast(1.06)${blur ? ` blur(${blur}px)` : ""}`,
                  transform: blur ? "scale(1.04)" : undefined }} />
    <div className="absolute inset-0" style={{ background: `rgba(9,15,40,${dim})` }} />
    {vignette && (
      <div className="absolute inset-0" style={{ background:
        "radial-gradient(120% 90% at 50% 40%, transparent 35%, rgba(7,12,30,0.72) 100%)" }} />
    )}
  </div>
);

/** A kicker + headline sitting over photography or a screenshot. */
const Caption = ({ kicker, title, sub, align = "left" }) => (
  <div className={`absolute inset-0 flex flex-col justify-end px-8 sm:px-14 lg:px-20 pb-24 sm:pb-28
                   ${align === "center" ? "items-center text-center" : ""}`}>
    {kicker && (
      <p className="tour-rise-1 text-gold text-[10px] sm:text-[11px] font-semibold uppercase tracking-[0.3em]">
        {kicker}
      </p>
    )}
    {title && (
      <h2 className="tour-rise-2 mt-3 max-w-3xl font-display text-2xl sm:text-4xl lg:text-5xl
                     font-bold leading-tight text-white">
        {title}
      </h2>
    )}
    {sub && <p className="tour-rise-3 mt-4 max-w-xl text-sm sm:text-base text-blue-50/80">{sub}</p>}
  </div>
);

/** A vertical chain of operational steps that lights up one stage at a time. */
const Chain = ({ steps, progress, title }) => {
  const lit = Math.min(steps.length, Math.round(steps.length * Math.min(1, progress * 1.15 + 0.12)));
  return (
    <div className="absolute right-4 sm:right-10 lg:right-16 top-1/2 -translate-y-1/2
                    w-[16rem] sm:w-[22rem]">
      {title && (
        <p className="mb-3 text-[10px] font-semibold uppercase tracking-[0.26em] text-gold">{title}</p>
      )}
      <div className="space-y-1.5">
        {steps.map((label, i) => {
          const on = i < lit;
          return (
            <div key={label} data-testid={`chain-${i}`}
                 className="rounded-lg border px-3.5 py-2.5"
                 style={{
                   transition: "opacity 600ms ease-out, transform 600ms ease-out, background 600ms",
                   borderColor: on ? "rgba(212,175,55,0.5)" : "rgba(255,255,255,0.08)",
                   background: on ? "rgba(212,175,55,0.12)" : "rgba(255,255,255,0.03)",
                   opacity: on ? 1 : 0.3,
                   transform: on ? "translateX(0)" : "translateX(14px)",
                 }}>
              <p className={`text-xs sm:text-sm font-semibold ${on ? "text-white" : "text-blue-100/60"}`}>
                {label}
              </p>
            </div>
          );
        })}
      </div>
    </div>
  );
};

/** Slow cinematic move across a real screenshot of the running product. */
export const Shot = ({ name, label, move = "in", focus = "center", dim = 0.35 }) => (
  <div className="absolute inset-0 overflow-hidden">
    <img
      src={`${SHOT}/${name}.jpg?v=${SHOT_V}`}
      alt={label || "Cornerstone Ops"}
      loading="lazy"
      className={`h-full w-full tour-shot tour-ken tour-ken-${move}`}
      style={{ objectPosition: focus }}
    />
    <div className="absolute inset-0" style={{ background: `rgba(13,21,51,${dim})` }} />
    <div className="absolute inset-0 bg-gradient-to-t from-royal-deep via-royal-deep/25 to-transparent" />
    {label && (
      <p className="absolute left-6 sm:left-10 top-6 sm:top-8 text-gold text-[10px] sm:text-[11px]
                    tracking-[0.28em] uppercase font-semibold tour-fade">
        {label}
      </p>
    )}
  </div>
);

const CHAOS_NOISE = [
  "Which property am I at next?",
  "Cleaner running 40 min late",
  "Owner: any update on 412 Harbor?",
  "Invoice still unpaid — 3rd reminder",
  "Plumber can't find the lockbox code",
  "Inspection photos are in my texts",
  "Was the turnover done or not?",
  "Spreadsheet v7_FINAL(2).xlsx",
  "Double-booked Saturday again",
  "Who has the gate code?",
  "Maintenance issue found — tell who?",
  "Calendar #3 says something different",
];

export const ChaosScene = ({ progress }) => (
  <div className="absolute inset-0 bg-royal-deep sidebar-grain overflow-hidden" data-testid="scene-chaos">
    <div className="absolute inset-0 opacity-[0.18]">
      <Shot name="messages" dim={0.55} move="drift" />
    </div>
    {CHAOS_NOISE.map((text, i) => {
      const shown = progress > (i / CHAOS_NOISE.length) * 0.85;
      const col = i % 3;
      return (
        <div
          key={text}
          aria-hidden={!shown}
          className={`absolute max-w-[15rem] rounded-lg border border-white/10 bg-white/[0.07]
                      px-3.5 py-2.5 transition-all duration-700
                      ${shown ? "opacity-100 tour-jitter" : "opacity-0 translate-y-4"}`}
          style={{
            left: `${6 + col * 31 + ((i * 7) % 9)}%`,
            top: `${11 + Math.floor(i / 3) * 21 + ((i * 5) % 7)}%`,
            transform: `rotate(${(i % 2 ? 1 : -1) * (1 + (i % 3))}deg)`,
            animationDelay: `${i * 0.19}s`,
          }}
        >
          <p className="text-[11px] sm:text-xs text-blue-50/90 leading-snug">{text}</p>
        </div>
      );
    })}
    <div
      className="absolute inset-0 transition-opacity duration-1000"
      style={{ opacity: 0.25 + progress * 0.5, background:
        "radial-gradient(circle at 50% 55%, rgba(220,38,38,0.16), transparent 62%)" }}
    />
  </div>
);

export const QuestionScene = () => (
  <div className="absolute inset-0 bg-[#070c1e] flex items-center justify-center" data-testid="scene-question">
    <div className="tour-pulse-slow h-40 w-40 rounded-full"
         style={{ background: "radial-gradient(circle, rgba(212,175,55,0.22), transparent 70%)" }} />
  </div>
);

export const RevealScene = ({ progress }) => (
  <div className="absolute inset-0 bg-royal-deep sidebar-grain overflow-hidden" data-testid="scene-reveal">
    <div className="absolute inset-0 transition-opacity duration-[1600ms]"
         style={{ opacity: Math.min(progress * 2.4, 1) }}>
      <Shot name="dashboard" label="Cornerstone Ops · Command Center" move="in" dim={0.42} />
    </div>
    <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6">
      <div className="tour-rise-1"><BrandBadge size="h-16 w-16 sm:h-20 sm:w-20" icon="h-11 w-11 sm:h-14 sm:w-14" /></div>
      <h2 className="tour-rise-2 font-display text-3xl sm:text-5xl lg:text-6xl font-bold text-white mt-5">
        Cornerstone <span className="text-gold">Ops</span>
      </h2>
      <div className="tour-rise-3 h-px w-40 sm:w-64 gold-rule mt-5" />
      <p className="tour-rise-3 text-blue-100/80 text-xs sm:text-sm tracking-[0.24em] uppercase mt-4">
        The Operational Command Center
      </p>
    </div>
  </div>
);

const PILLAR_ICONS = {
  schedules: CalendarDays, properties: Home, people: Users, teams: Users,
  inspections: ClipboardCheck, issues: AlertTriangle, vendors: Wrench,
  messaging: MessageSquare, invoices: Receipt, payments: CreditCard,
  workflows: Zap, automation: Zap,
};

/* Each pillar is driven by the narration line that names it: it flies in on the word and only
   converges into the logo once that line has finished. */
export const ConvergeScene = ({ progress, time = 0, cues = [], cueIndex = 0 }) => {
  const spoken = cues.filter((c) => c.scene === "converge" && /^Your\s/i.test(c.text));
  const items = spoken.map((c) => {
    const key = c.text.replace(/^Your\s+/i, "").replace(/\.$/, "").trim().toLowerCase();
    return { key, label: key.charAt(0).toUpperCase() + key.slice(1), cue: c,
             icon: PILLAR_ICONS[key] || Zap };
  });
  const lastConverge = spoken.length ? cues.indexOf(spoken[spoken.length - 1]) : 0;

  return (
  <div className="absolute inset-0 bg-royal-deep sidebar-grain overflow-hidden" data-testid="scene-converge">
    <div className="absolute inset-0 opacity-30"><Shot name="reports" dim={0.6} move="drift" /></div>
    <div className="relative h-full w-full flex items-center justify-center">
      <div className="absolute h-32 w-32 sm:h-44 sm:w-44 rounded-full tour-pulse-slow"
           style={{ background: "radial-gradient(circle, rgba(212,175,55,0.3), transparent 70%)" }} />
      <div className="relative z-10 flex flex-col items-center">
        <BrandBadge size="h-14 w-14 sm:h-16 sm:w-16" icon="h-10 w-10 sm:h-11 sm:w-11" />
        <p className="font-display text-white text-sm sm:text-base font-semibold mt-2.5">Cornerstone Ops</p>
      </div>
      {items.map((p, i) => {
        const at = cues.indexOf(p.cue);
        const named = at <= cueIndex;                      // the narrator has reached this word
        const arrived = at < cueIndex;                     // he has moved on: draw it into the logo
        const angle = (i / items.length) * Math.PI * 2 - Math.PI / 2;
        const r = 42;
        return (
          <div
            key={p.key}
            data-testid={`converge-${p.key}`}
            className="absolute flex items-center gap-2 rounded-full border border-gold/30 bg-royal-deep/85
                       px-3 py-1.5"
            style={{
              transition: "transform 320ms ease-out, opacity 240ms ease-out",
              left: "50%", top: "50%",
              transform: arrived
                ? "translate(-50%,-50%) scale(0.65)"
                : named
                  ? `translate(calc(-50% + ${Math.cos(angle) * r}vmin), calc(-50% + ${Math.sin(angle) * r}vmin)) scale(1)`
                  : `translate(calc(-50% + ${Math.cos(angle) * (r + 12)}vmin), calc(-50% + ${Math.sin(angle) * (r + 12)}vmin)) scale(0.8)`,
              opacity: arrived ? 0 : named ? 1 : 0,
            }}
          >
            <p.icon className="h-3.5 w-3.5 text-gold" />
            <span className="text-[11px] sm:text-xs font-semibold text-white whitespace-nowrap">{p.label}</span>
          </div>
        );
      })}
    </div>
    <div className="absolute inset-x-0 bottom-8 sm:bottom-12 flex flex-wrap items-center justify-center gap-2 px-6">
      {["All connected", "All visible", "All in one place"].map((t, i) => (
        <span key={t}
          className={`rounded-full border border-gold/40 bg-gold/10 px-3.5 py-1.5 text-[11px] sm:text-xs
                      font-semibold text-gold transition-all duration-700
                      ${lastConverge < cueIndex - i
                        ? "opacity-100" : "opacity-0 translate-y-2"}`}>
          {t}
        </span>
      ))}
    </div>
  </div>
  );
};

const STEPS = [
  ["Property added", "properties"], ["Scheduled", "schedule"], ["Team assigned", "team"],
  ["Crew notified", "messages"], ["Property details on site", "properties"],
  ["Work completed", "schedule"], ["Inspection performed", "inspections"],
  ["Issue documented", "issues"], ["Right person notified", "messages"],
  ["Vendor coordinated", "vendors"], ["Work tracked", "dashboard"],
  ["Invoice generated", "invoices"], ["Payment processed", "invoices"],
  ["Records stored", "inspections"], ["Full history visible", "reports"],
];

export const WorkflowScene = ({ progress }) => {
  const active = Math.min(Math.floor(progress * STEPS.length), STEPS.length - 1);
  return (
    <div className="absolute inset-0 bg-royal-deep overflow-hidden" data-testid="scene-workflow">
      <Shot name={STEPS[active][1]} label={`Live workflow · step ${active + 1} of ${STEPS.length}`}
            move={active % 2 ? "drift" : "in"} dim={0.55} />
      <div className="absolute inset-x-0 bottom-0 p-5 pb-20 sm:p-10 sm:pb-20">
        <div className="flex flex-wrap gap-1.5 sm:gap-2">
          {STEPS.map(([label], i) => (
            <span key={label}
              className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[10px] sm:text-[11px]
                          font-semibold transition-all duration-500
                          ${i < active ? "border-gold/40 bg-gold/15 text-gold"
                            : i === active ? "border-gold bg-gold text-royal-deep scale-105"
                            : "border-white/15 bg-white/5 text-blue-100/45"}`}>
              {i < active && <Check className="h-3 w-3" />}
              {label}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
};

export const AutomationScene = ({ progress }) => (
  <div className="absolute inset-0 bg-royal-deep overflow-hidden" data-testid="scene-automation">
    <Shot name="automations" label="Automations · when this, then that" move="in" dim={0.5} />
    <div className="absolute right-4 sm:right-10 top-1/2 -translate-y-1/2 w-[15rem] sm:w-[21rem] space-y-2.5">
      {[
        ["Turnover completed", "Owner report sent"],
        ["Issue marked urgent", "Manager alerted instantly"],
        ["Checkout synced", "Turnover clean scheduled"],
        ["Work order closed", "Invoice drafted"],
      ].map(([when, then], i) => (
        <div key={when}
          className={`rounded-lg border border-gold/25 bg-royal-deep/90 p-3
                      transition-all duration-700 ${progress > 0.15 + i * 0.18
                        ? "opacity-100 translate-x-0" : "opacity-0 translate-x-6"}`}>
          <p className="text-[10px] uppercase tracking-[0.18em] text-gold font-semibold">When</p>
          <p className="text-xs sm:text-sm text-white font-medium">{when}</p>
          <div className="my-1.5 flex items-center gap-1.5 text-gold">
            <ArrowRight className="h-3.5 w-3.5" />
            <span className="text-[10px] uppercase tracking-[0.18em] font-semibold">Then</span>
          </div>
          <p className="text-xs sm:text-sm text-blue-50/90">{then}</p>
        </div>
      ))}
    </div>
  </div>
);

export const VisibilityScene = ({ progress }) => (
  <div className="absolute inset-0 bg-royal-deep overflow-hidden" data-testid="scene-visibility">
    <Shot name={progress > 0.55 ? "reports" : "dashboard"} label="One screen · the whole operation"
          move="in" dim={0.42} />
    <div className="absolute inset-x-0 bottom-0 p-5 pb-20 sm:p-10 sm:pb-20 grid grid-cols-2 sm:grid-cols-4 gap-2.5 sm:gap-3">
      {[["Active properties", "Live"], ["Today's assignments", "Tracked"],
        ["Open issues", "Visible"], ["Financial activity", "Current"]].map(([label, value], i) => (
        <div key={label}
          className={`rounded-lg border border-white/12 bg-white/[0.07] p-3
                      transition-all duration-700 ${progress > 0.12 + i * 0.14
                        ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
          <p className="text-[9px] sm:text-[10px] uppercase tracking-[0.16em] text-blue-100/60 font-semibold">{label}</p>
          <p className="font-display text-base sm:text-xl font-bold text-gold mt-1">{value}</p>
        </div>
      ))}
    </div>
  </div>
);

export const ScaleScene = ({ progress }) => {
  // These numbers must be the exact numbers the narrator says ("Eight properties or eight
  // hundred") — anything else reads as a mistake on screen.
  const marks = [8, 800];
  const step = Math.min(Math.floor(progress * marks.length), marks.length - 1);
  const tiles = [12, 120][step];
  return (
    <div className="absolute inset-0 bg-royal-deep overflow-hidden" data-testid="scene-scale">
      <div className="absolute inset-0 opacity-40"><Shot name="properties" dim={0.72} move="drift" /></div>
      <div className="relative h-full flex flex-col items-center justify-center gap-6 px-6 pb-16">
        <div className="flex items-baseline gap-3">
          <span className="font-display text-5xl sm:text-7xl font-bold text-gold tabular-nums">
            {marks[step]}{step === marks.length - 1 ? "+" : ""}
          </span>
          <span className="text-blue-100/70 text-xs sm:text-sm uppercase tracking-[0.22em]">properties</span>
        </div>
        <div className="grid grid-cols-12 sm:grid-cols-[repeat(20,minmax(0,1fr))] gap-1 sm:gap-1.5 max-w-3xl">
          {Array.from({ length: tiles }).map((_, i) => (
            <span key={i} className="h-2 sm:h-2.5 rounded-sm bg-gold/70 tour-tile"
                  style={{ animationDelay: `${(i % 20) * 0.03}s` }} />
          ))}
        </div>
        <p className="text-blue-100/60 text-[11px] sm:text-xs tracking-[0.2em] uppercase">
          Same clarity at every size
        </p>
      </div>
    </div>
  );
};

export const HumanScene = () => (
  <div className="absolute inset-0 bg-royal-deep overflow-hidden" data-testid="scene-human">
    <Shot name="schedule" label="Clarity for the people doing the work" move="drift" dim={0.58} focus="30% 40%" />
    <div className="absolute inset-x-0 bottom-0 p-6 pb-20 sm:p-12 sm:pb-20 flex flex-wrap gap-2.5">
      {["Teams know exactly where to be", "Managers keep control",
        "Owners get confidence"].map((t) => (
        <span key={t} className="rounded-full border border-white/15 bg-white/[0.08] px-3.5 py-1.5
                                 text-[11px] sm:text-xs font-medium text-blue-50/90 tour-fade">
          {t}
        </span>
      ))}
    </div>
  </div>
);

export const StatementScene = ({ progress }) => (
  <div className="absolute inset-0 bg-royal-deep sidebar-grain flex items-center justify-center px-6" data-testid="scene-statement">
    <div className="text-center">
      {["ONE PLATFORM.", "ONE SOURCE OF TRUTH.", "ONE OPERATION."].map((line, i) => (
        <p key={line}
          className={`font-display text-2xl sm:text-4xl lg:text-5xl font-bold leading-[1.15] transition-all duration-700
                      ${i === 2 ? "text-gold" : "text-white"}
                      ${progress > 0.12 + i * 0.16 ? "opacity-100 translate-y-0" : "opacity-0 translate-y-5"}`}>
          {line}
        </p>
      ))}
      <div className={`h-px gold-rule mx-auto my-6 transition-all duration-1000
                       ${progress > 0.62 ? "w-56 sm:w-80 opacity-100" : "w-0 opacity-0"}`} />
      <p className={`text-blue-100/80 text-xs sm:text-base tracking-[0.28em] uppercase transition-opacity duration-1000
                     ${progress > 0.7 ? "opacity-100" : "opacity-0"}`}>
        Everything you need. All in one place.
      </p>
    </div>
  </div>
);

export const CtaScene = ({ progress, onStart, onExplore }) => (
  <div className="absolute inset-0 bg-royal-deep sidebar-grain overflow-hidden flex items-center justify-center px-6" data-testid="scene-cta">
    <div className="absolute inset-0 opacity-25"><Shot name="dashboard" dim={0.65} move="drift" /></div>
    <div className="relative text-center">
      <div className="flex justify-center"><BrandBadge size="h-14 w-14" icon="h-10 w-10" /></div>
      <h2 className="font-display text-2xl sm:text-4xl font-bold text-white mt-4">
        Welcome to Cornerstone <span className="text-gold">Ops</span>
      </h2>
      <p className="text-blue-100/70 text-xs sm:text-sm tracking-[0.2em] uppercase mt-3">
        Build your operation on a stronger foundation
      </p>
      <div className={`mt-8 flex flex-col sm:flex-row items-center justify-center gap-3 transition-all duration-700
                       ${progress > 0.3 ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
        <button data-testid="tour-cta-start" onClick={onStart}
          className="h-12 w-full sm:w-auto rounded-full bg-gold px-8 font-display text-sm font-bold uppercase
                     tracking-[0.16em] text-royal-deep transition-transform duration-200 hover:scale-[1.03]">
          Start With Cornerstone
        </button>
        <button data-testid="tour-cta-explore" onClick={onExplore}
          className="h-12 w-full sm:w-auto rounded-full border border-white/30 px-8 font-display text-sm font-bold
                     uppercase tracking-[0.16em] text-white transition-colors duration-200 hover:bg-white/10">
          Explore The Platform
        </button>
      </div>
      <div data-testid="tour-cta-verse"
           className={`mt-9 transition-all duration-1000 ${progress > 0.5 ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3"}`}>
        <p className="font-display text-[11px] sm:text-xs font-bold uppercase tracking-[0.28em] text-gold">
          Colossians 3:23
        </p>
        <p className="mx-auto mt-2.5 max-w-xl font-display text-sm sm:text-base italic leading-relaxed text-gold">
          "Whatever you do, work at it with all your heart, as working for the Lord,
          not for human masters."
        </p>
      </div>
    </div>
  </div>
);

/* ---- Guided walkthrough: one scene per module, points revealed line by line ---- */
const MODULES = {
  weather: { shot: "weather", label: "Weather intelligence · property by property",
    title: "Weather becomes operational intelligence",
    points: ["National Weather Service data per property",
             "National Hurricane Center tropical tracking",
             "Freeze, wind, storm and surge watches",
             "Playbook checklists the moment a watch lands",
             "You act the day before, not the morning after"] },
  push: { shot: "push", label: "Push notifications · the operation stays informed",
    title: "The right person knows, right now",
    points: ["Work order assigned or moved", "Urgent issue raised at a property",
             "Freeze and storm alerts", "Straight to the phone — no text charges",
             "Tap it and land on the exact record"] },
  owner: { shot: "reports", label: "Owner visibility · earned trust",
    title: "Owners see a company in control",
    points: ["Condition briefing per property",
             "Emailed to the owner in one click",
             "Work completed, issues found, action taken",
             "Professional reporting, not a text thread"] },
  directions: { shot: "properties", label: "Field navigation · no address hunting",
    title: "Turn-by-turn from the work order",
    points: ["One tap to Google Maps directions",
             "Access notes and codes travel with the job",
             "The address and the codes are already on the job"] },
  properties: { shot: "properties", label: "Properties · the record of record",
    title: "Every property, fully documented",
    points: ["Address, bedrooms, baths, owner", "Access notes, lockbox codes, Wi-Fi",
             "Photos & condition rating", "Booking calendar sync for turnovers"] },
  schedule: { shot: "schedule", label: "Schedule & work orders · one queue",
    title: "All the work, on one schedule",
    points: ["Cleaning · Turnover · Inspection · Maintenance", "Repair · Restocking · Vendor · Emergency",
             "List view or month at a glance", "Assign person, time, duration, cost",
             "Repeats weekly, biweekly, monthly",
             "Pending → Assigned → In progress → Complete"] },
  team: { shot: "team", label: "Team & roles · the right visibility",
    title: "Everyone sees exactly what they should",
    points: ["Invite by email in seconds", "Owners, admins, managers, leads",
             "Cleaners, vendors, property owners, clients", "Managers see all · owners see theirs · crews see their work"] },
  inspections: { shot: "inspections", label: "Inspections · proof of quality",
    title: "Quality you can actually prove",
    points: ["Condition scoring", "Missing items & safety concerns", "Photo proof attached",
             "Condition history & trend", "Recurring problems surfaced"] },
  issues: { shot: "issues", label: "Issues · nothing gets lost",
    title: "A problem becomes a tracked workflow",
    points: ["Reported from anywhere", "Low · Medium · High · Urgent",
             "Urgent alerts managers instantly", "Becomes a work order, assigned and tracked"] },
  vendors: { shot: "vendors", label: "Vendors · trades without phone tag",
    title: "Outside trades, inside your operation",
    points: ["Plumbing, electrical, HVAC, landscaping & more", "Per job, hourly, or they invoice you",
             "Insurance expiry tracked", "Payouts approved and paid on record"] },
  invoices: { shot: "invoices", label: "Invoices & payments · get paid",
    title: "The work you did, billed and paid",
    points: ["Generate weekly & monthly billing", "Invoice owners or guests directly",
             "Secure card payment link", "Marked paid automatically"] },
  finance: { shot: "reports", label: "Finance & reporting · the numbers",
    title: "Know exactly where you stand",
    points: ["Revenue, cost, profitability", "Completion rate & average fix time",
             "Team performance & condition scores", "Automatic weekly owner reports"] },
  messages: { shot: "messages", label: "Messaging · where the work is",
    title: "Communication attached to the work",
    points: ["Direct messages", "Group threads", "Property conversations kept on the property"] },
  mobile: { shot: "schedule", label: "In the field · installs like an app",
    title: "Your whole team, in their pocket",
    points: ["Installs on any phone", "Job, property, access code, checklist",
             "Marked complete in the field", "Your office sees it instantly"] },
  trust: { shot: "dashboard", label: "Trust · built to be relied on",
    title: "The questions everyone asks",
    points: ["Your data is fully separated & recorded", "Running today, not in six months",
             "Christian, family-owned", "Built on the Outer Banks of North Carolina"] },
  pricing: { shot: "dashboard", label: "Pricing · priced to your operation",
    title: "Simple, sized to your portfolio",
    points: ["Starter · Professional · Business", "Monthly, or save with annual",
             "Custom Enterprise agreements", "14-day free trial"] },
};

const MODULE_FALLBACK = { shot: "dashboard", label: "Cornerstone Ops",
  title: "One connected operation", points: [] };

const ModuleScene = ({ scene, time = 0, cues = [], cueIndex = 0 }) => {
  // A scene name the build does not know about must never take the player down with it.
  const m = MODULES[scene] || MODULE_FALLBACK;
  const list = cues.filter((c) => c.scene === scene);
  const globalIdx = cueIndex >= 0 ? cueIndex : 0;
  // Count how many of this scene's lines the narrator has actually reached.
  const spokenCount = list.length
    ? Math.max(0, list.filter((c) => cues.indexOf(c) <= globalIdx).length)
    : 0;
  // Spread the points across however many lines this scene has, so the last one always lands.
  const revealed = list.length
    ? Math.ceil((m.points.length * spokenCount) / list.length)
    : 0;
  return (
    <div className="absolute inset-0 bg-royal-deep overflow-hidden" data-testid={`scene-${scene}`}>
      <Shot name={m.shot} label={m.label} move="drift" dim={0.42} />
      <div className="absolute inset-0 bg-gradient-to-r from-royal-deep via-royal-deep/80 to-transparent" />
      <div className="absolute inset-0 flex flex-col justify-center px-8 sm:px-14 lg:px-20 max-w-[62%] sm:max-w-[54%]">
        <h2 className="font-display text-2xl sm:text-4xl lg:text-5xl font-bold text-white max-w-2xl
                       leading-tight tour-rise-1">
          {m.title}
        </h2>
        <div className="mt-6 sm:mt-8 h-px w-24 sm:w-40 gold-rule tour-rise-2" />
        <ul className="mt-6 sm:mt-8 space-y-2.5 sm:space-y-3.5">
          {m.points.map((p, i) => {
            const shown = revealed > i;
            return (
              <li key={p} data-testid={`point-${scene}-${i}`}
                  className="flex items-center gap-3 transition-all duration-700 ease-out"
                  style={{ opacity: shown ? 1 : 0,
                           transform: shown ? "translateX(0)" : "translateX(-14px)" }}>
                <span className="h-1.5 w-1.5 rounded-full bg-gold shrink-0" />
                <span className="text-blue-50 text-sm sm:text-base lg:text-lg font-medium">{p}</span>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
};

const moduleScene = (key) => {
  const C = (props) => <ModuleScene scene={key} {...props} />;
  C.displayName = `ModuleScene(${key})`;
  return C;
};

export const StatementBridge = ({ time = 0 }) => (
  <div className="absolute inset-0 bg-royal-deep sidebar-grain overflow-hidden flex items-center
                  justify-center px-8" data-testid="scene-statement2">
    <div className="absolute inset-0 opacity-20"><Shot name="dashboard" dim={0.7} move="in" /></div>
    <p className="relative font-display text-2xl sm:text-4xl lg:text-5xl font-bold text-white
                  text-center max-w-3xl tour-rise-1">
      So let me show you <span className="text-gold">exactly how it works</span>.
    </p>
  </div>
);

/** The operation, connected. Eleven stages light up in sequence as the narrator walks them,
    each feeding the command center at the middle — the whole point of the product in one frame. */
const FLOW = [
  "Property", "Reservation", "Automation", "Job", "Team", "Inspection",
  "Report", "Owner", "Vendor", "Payment", "Intelligence", "Action",
];

export const ConnectedScene = ({ progress = 0 }) => {
  const lit = Math.round(FLOW.length * Math.min(1, progress * 1.12));
  return (
    <div className="absolute inset-0 bg-royal-deep sidebar-grain overflow-hidden"
         data-testid="scene-connected">
      <div className="absolute inset-0 opacity-[0.18]"><Shot name="dashboard" dim={0.75} move="drift" /></div>
      <div className="relative h-full flex flex-col items-center justify-center px-6 sm:px-10">
        <p className="text-[10px] sm:text-xs uppercase tracking-[0.3em] text-gold/80">
          One connected operation
        </p>
        <div className="mt-5 sm:mt-7 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 sm:gap-3
                        max-w-4xl w-full">
          {FLOW.map((stage, i) => {
            const on = i < lit;
            return (
              <div key={stage} data-testid={`flow-${i}`}
                   className="rounded-lg border px-3 py-2.5 sm:py-3 text-center transition-all duration-700 ease-out"
                   style={{
                     borderColor: on ? "rgba(212,175,55,0.55)" : "rgba(255,255,255,0.10)",
                     background: on ? "rgba(212,175,55,0.10)" : "rgba(255,255,255,0.02)",
                     opacity: on ? 1 : 0.35,
                     transform: on ? "translateY(0)" : "translateY(8px)",
                     transitionDelay: `${(i % 4) * 60}ms`,
                   }}>
                <span className={`text-xs sm:text-sm font-semibold ${on ? "text-white" : "text-blue-100/60"}`}>
                  {stage}
                </span>
              </div>
            );
          })}
        </div>
        <div className="mt-6 sm:mt-9 flex flex-col items-center">
          <div className="h-8 sm:h-12 w-px bg-gradient-to-b from-gold/10 via-gold/60 to-gold" />
          <div className="mt-4 rounded-xl border border-gold/50 bg-royal/40 px-5 sm:px-8 py-3 sm:py-4
                          text-center">
            <p className="font-display text-lg sm:text-2xl lg:text-3xl font-bold text-white">
              Cornerstone Ops
            </p>
            <p className="mt-0.5 text-[10px] sm:text-xs uppercase tracking-[0.24em] text-gold">
              The Operational Command Center
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ---------------- The 2026 welcome film: real world, then the platform ---------------- */

export const CoastScene = ({ progress = 0 }) => (
  <div className="absolute inset-0 bg-[#070c1e] overflow-hidden" data-testid="scene-coast">
    <Photo name="coast_hero" move="in" dim={0.32} focus="50% 42%" />
    <Caption kicker="The property is the easy part"
             title={progress > 0.5 ? "Everything behind it is an operation" : "One property. One guest. One week."} />
  </div>
);

export const ChannelScene = ({ progress = 0 }) => (
  <div className="absolute inset-0 bg-royal-deep overflow-hidden" data-testid="scene-channels">
    <Shot name="properties" label="Property · booking calendar" move="drift" dim={0.5} />
    <div className="absolute inset-0 bg-gradient-to-r from-royal-deep via-royal-deep/85 to-transparent" />
    <div className="absolute inset-y-0 right-0 w-[42%] bg-gradient-to-l from-royal-deep via-royal-deep/85 to-transparent" />
    <div className="absolute inset-0 flex flex-col justify-center px-8 sm:px-14 lg:px-20 max-w-[54%]">
      <p className="tour-rise-1 text-gold text-[10px] sm:text-[11px] font-semibold uppercase tracking-[0.3em]">
        Airbnb &amp; Vrbo · iCal
      </p>
      <h2 className="tour-rise-2 mt-3 font-display text-2xl sm:text-4xl lg:text-[2.8rem] font-bold
                     leading-tight text-white">
        Your bookings drive the operation
      </h2>
      <div className="tour-rise-3 mt-6 rounded-xl border border-gold/30 bg-royal-deep/85 p-4">
        <p className="text-[10px] uppercase tracking-[0.2em] text-blue-100/60 font-semibold">
          Booking calendar link
        </p>
        <p className="mt-1.5 truncate font-mono text-[11px] sm:text-xs text-gold">
          https://www.airbnb.com/calendar/ical/…ics
        </p>
        <p className="mt-3 text-[10px] uppercase tracking-[0.2em] text-blue-100/60 font-semibold">
          Next checkout
        </p>
        <p className="mt-1 text-sm sm:text-base font-semibold text-white">Saturday · 10:00 AM</p>
      </div>
    </div>
    <Chain progress={progress} title="Automatically"
           steps={["Reservation read from the feed", "Checkout date recognised",
                   "Turnover work order created", "Assigned to the crew",
                   "Inspection scheduled after", "Property status updated"]} />
  </div>
);

export const FieldScene = ({ progress = 0 }) => (
  <div className="absolute inset-0 bg-[#070c1e] overflow-hidden" data-testid="scene-field">
    <Photo name="interior" move="drift" dim={0.5} focus="60% 45%" />
    <p className="absolute left-8 sm:left-14 top-7 sm:top-9 text-gold text-[10px] sm:text-[11px]
                  font-semibold uppercase tracking-[0.3em] tour-fade">
      In the field · on any phone
    </p>
    <div className="absolute left-4 sm:left-10 top-1/2 -translate-y-1/2 w-[16rem] sm:w-[20rem]
                    rounded-2xl border border-white/15 bg-royal-deep/90 p-4">
      <p className="text-[10px] uppercase tracking-[0.22em] text-gold font-semibold">Turnover clean</p>
      <p className="mt-1 font-display text-base sm:text-lg font-bold text-white">Harbor Point Cottage</p>
      <p className="text-[11px] text-blue-100/70">Today · 11:00 AM · 2h</p>
      <div className="mt-3 space-y-1.5">
        {["Beds stripped & remade", "Kitchen & baths reset", "Linens restocked",
          "Photos attached", "Marked complete"].map((t, i) => {
          const done = progress > 0.1 + i * 0.16;
          return (
            <div key={t} className="flex items-center gap-2 transition-opacity duration-500"
                 style={{ opacity: done ? 1 : 0.35 }}>
              <span className={`flex h-4 w-4 items-center justify-center rounded-full border
                                ${done ? "border-gold bg-gold" : "border-white/30"}`}>
                {done && <Check className="h-2.5 w-2.5 text-royal-deep" />}
              </span>
              <span className="text-[11px] sm:text-xs text-blue-50/90">{t}</span>
            </div>
          );
        })}
      </div>
    </div>
  </div>
);

export const VendorFlowScene = ({ progress = 0 }) => (
  <div className="absolute inset-0 bg-royal-deep overflow-hidden" data-testid="scene-vendorflow">
    <Photo name="tools" move="in" dim={0.62} focus="50% 50%" />
    <div className="absolute inset-0 bg-gradient-to-r from-royal-deep via-royal-deep/80 to-transparent" />
    <div className="absolute inset-y-0 right-0 w-[42%] bg-gradient-to-l from-royal-deep via-royal-deep/80 to-transparent" />

    <div className="absolute inset-0 flex flex-col justify-center px-8 sm:px-14 lg:px-20 max-w-[52%]">
      <p className="tour-rise-1 text-gold text-[10px] sm:text-[11px] font-semibold uppercase tracking-[0.3em]">
        Issue · work order · vendor
      </p>
      <h2 className="tour-rise-2 mt-3 font-display text-2xl sm:text-4xl lg:text-[2.7rem] font-bold
                     leading-tight text-white">
        A problem becomes a documented workflow
      </h2>
    </div>
    <Chain progress={progress} title="Cracked window seal"
           steps={["Found on inspection", "Logged as a tracked issue", "Work order created",
                   "Vendor assigned with access notes", "Completed & photographed",
                   "Stored on the property record"]} />
  </div>
);

const PROPS_WEATHER = [
  { photo: "beach_house", name: "Oceanfront · Coastal", head: "Tropical system tracked",
    lines: ["NHC track 140 mi offshore", "Coastal flood advisory · surge at high tide",
            "Secure exterior furniture"] },
  { photo: "cabin", name: "Ridge Cabin · Inland", head: "Hard freeze tonight",
    lines: ["Low 24°F by 2:00 AM", "Exposed plumbing risk", "Wrap spigots, check crawl space"] },
];

export const WeatherTwoScene = ({ progress = 0 }) => (
  <div className="absolute inset-0 bg-[#070c1e] overflow-hidden" data-testid="scene-weathertwo">
    <div className="absolute inset-0 grid grid-cols-2">
      {PROPS_WEATHER.map((p, i) => (
        <div key={p.name} className="relative overflow-hidden border-r border-white/10">
          <Photo name={p.photo} move={i ? "drift" : "in"} dim={0.52} vignette={false} />
          <div className="absolute inset-x-0 bottom-0 p-4 sm:p-7 pb-20 sm:pb-24">
            <p className="text-[10px] uppercase tracking-[0.22em] text-gold font-semibold">{p.name}</p>
            <p className="mt-2 font-display text-lg sm:text-2xl font-bold text-white">{p.head}</p>
            <ul className="mt-3 space-y-1.5">
              {p.lines.map((l, j) => (
                <li key={l} className="flex items-center gap-2 transition-all duration-700"
                    style={{ opacity: progress > 0.15 + j * 0.2 ? 1 : 0,
                             transform: progress > 0.15 + j * 0.2 ? "none" : "translateY(6px)" }}>
                  <span className="h-1.5 w-1.5 rounded-full bg-gold shrink-0" />
                  <span className="text-[11px] sm:text-sm text-blue-50/90">{l}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      ))}
    </div>
    <div className="absolute inset-x-0 top-5 sm:top-8 flex justify-center">
      <span className="rounded-full border border-gold/40 bg-royal-deep/85 px-4 py-1.5 text-[10px]
                       sm:text-xs font-semibold uppercase tracking-[0.24em] text-gold">
        Each property · its own location · its own intelligence
      </span>
    </div>
  </div>
);

const BRIEFING = [
  ["Current conditions", "Wind 34 mph · seas building · rain bands offshore"],
  ["Tropical tracking", "National Hurricane Center · system 140 mi offshore"],
  ["Potential impact", "Exterior items, screened porch, standing water at the north side"],
  ["Operational considerations", "Crew access after 9 AM · turnover at risk"],
  ["Recommended actions", "Secure furniture, move the inspection up a day, confirm the crew"],
];

export const BriefingScene = ({ progress = 0 }) => (
  <div className="absolute inset-0 bg-[#070c1e] overflow-hidden" data-testid="scene-briefing">
    <Photo name="storm" move="drift" dim={0.58} focus="50% 40%" />
    <div className="absolute inset-0 flex items-center justify-center px-5 pb-16">
      <div className="w-full max-w-2xl rounded-2xl border border-gold/30 bg-royal-deep/92 p-5 sm:p-7
                      tour-rise-1">
        <p className="text-[10px] uppercase tracking-[0.26em] text-gold font-semibold">
          Property weather briefing
        </p>
        <p className="mt-1.5 font-display text-lg sm:text-2xl font-bold text-white">
          Oceanfront Vacation Rental
        </p>
        <div className="mt-4 space-y-2.5">
          {BRIEFING.map(([label, body], i) => (
            <div key={label} className="transition-all duration-700"
                 style={{ opacity: progress > 0.08 + i * 0.16 ? 1 : 0,
                          transform: progress > 0.08 + i * 0.16 ? "none" : "translateY(8px)" }}>
              <p className="text-[9px] sm:text-[10px] uppercase tracking-[0.2em] text-blue-100/55 font-semibold">
                {label}
              </p>
              <p className="text-[11px] sm:text-sm text-blue-50/95">{body}</p>
            </div>
          ))}
        </div>
        <div className="mt-5 flex items-center gap-2.5 transition-opacity duration-700"
             style={{ opacity: progress > 0.78 ? 1 : 0 }}>
          <span className="rounded-full bg-gold px-4 py-2 text-[10px] sm:text-xs font-bold uppercase
                           tracking-[0.16em] text-royal-deep">Email to owner</span>
          <span className="text-[10px] sm:text-xs text-blue-100/65">Sent · one click</span>
        </div>
      </div>
    </div>
  </div>
);

const ALERTS = [
  ["Weather alert", "Conditions may impact Oceanfront Vacation Rental."],
  ["Recommended action", "Secure exterior items and move the inspection to today."],
  ["Owner notified", "Briefing emailed to the property owner."],
  ["Crew updated", "Turnover moved to 9:00 AM — Maria acknowledged."],
];

export const PushPhoneScene = ({ progress = 0 }) => (
  <div className="absolute inset-0 bg-[#070c1e] overflow-hidden" data-testid="scene-pushphone">
    <Photo name="phone" move="in" dim={0.6} focus="35% 45%" blur={7} />
    <div className="absolute inset-y-0 right-0 flex w-full sm:w-[60%] flex-col justify-center gap-2.5
                    px-6 sm:px-12 pb-16">
      {ALERTS.map(([title, body], i) => {
        const on = progress > 0.05 + i * 0.22;
        return (
          <div key={title} data-testid={`alert-${i}`}
               className="rounded-2xl border border-white/15 bg-white/[0.09] p-3.5"
               style={{ transition: "opacity 500ms ease-out, transform 500ms ease-out",
                        opacity: on ? 1 : 0, transform: on ? "none" : "translateY(14px) scale(0.97)" }}>
            <p className="text-[9px] uppercase tracking-[0.2em] text-gold font-semibold">Cornerstone Ops</p>
            <p className="mt-1 text-xs sm:text-sm font-semibold text-white">{title}</p>
            <p className="text-[11px] sm:text-xs text-blue-50/85">{body}</p>
          </div>
        );
      })}
    </div>
  </div>
);

export const ClosingScene = ({ progress = 0, onStart }) => (
  <div className="absolute inset-0 bg-royal-deep sidebar-grain overflow-hidden" data-testid="scene-closing">
    <div className="absolute inset-0 opacity-30"><Photo name="coast_dune" move="drift" dim={0.68} /></div>
    <div className="relative h-full flex flex-col items-center justify-center px-6 text-center pb-8">
      <div className="tour-rise-1"><BrandBadge size="h-12 w-12 sm:h-14 sm:w-14" icon="h-9 w-9 sm:h-10 sm:w-10" /></div>
      <h2 className="tour-rise-2 mt-3 font-display text-2xl sm:text-4xl font-bold text-white">
        Cornerstone <span className="text-gold">Ops</span>
      </h2>
      <div className="tour-rise-3 mt-3 h-px w-40 sm:w-64 gold-rule" />
      <p className="tour-rise-3 mt-3 text-[10px] sm:text-xs uppercase tracking-[0.28em] text-blue-100/80">
        The Operational Command Center
      </p>
      <div className="mt-5 space-y-1">
        {["Connect the operation.", "Automate the work.", "See what's happening.",
          "Act before problems become problems."].map((line, i) => (
          <p key={line} className="font-display text-xs sm:text-base font-semibold text-white/90
                                   transition-all duration-700"
             style={{ opacity: progress > 0.2 + i * 0.14 ? 1 : 0,
                      transform: progress > 0.2 + i * 0.14 ? "none" : "translateY(8px)" }}>
            {line}
          </p>
        ))}
      </div>
      <button data-testid="tour-cta-start" onClick={onStart}
        className="mt-6 h-11 rounded-full bg-gold px-8 font-display text-xs sm:text-sm font-bold uppercase
                   tracking-[0.16em] text-royal-deep transition-transform duration-200 hover:scale-[1.03]"
        style={{ opacity: progress > 0.7 ? 1 : 0, transition: "opacity 700ms ease-out" }}>
        Start Free Trial
      </button>
      <div data-testid="tour-cta-verse" className="mt-7 transition-all duration-1000"
           style={{ opacity: progress > 0.78 ? 1 : 0,
                    transform: progress > 0.78 ? "none" : "translateY(10px)" }}>
        <p className="font-display text-[10px] sm:text-[11px] font-bold uppercase tracking-[0.28em] text-gold">
          Colossians 3:23
        </p>
        <p className="mx-auto mt-2 max-w-xl font-display text-xs sm:text-base italic leading-relaxed text-gold">
          "Whatever you do, work at it with all your heart, as working for the Lord,
          not for human masters."
        </p>
      </div>
    </div>
  </div>
);

const OLD_WAY = ["Spreadsheets", "Group texts", "Three calendars", "Paper checklists", "Phone tag"];

/** The turn: the old way struck out, the new standard named. Cornerstone Ops is the answer. */
export const BridgeScene = ({ progress = 0 }) => (
  <div className="absolute inset-0 bg-[#070c1e] sidebar-grain overflow-hidden" data-testid="scene-bridge">
    <div className="absolute inset-0 opacity-[0.14]"><Photo name="coast_dune" move="drift" dim={0.7} /></div>
    <div className="relative h-full flex flex-col items-center justify-center px-6 pb-16 text-center">
      <p className="text-[10px] sm:text-[11px] font-semibold uppercase tracking-[0.3em] text-blue-100/45">
        The old way
      </p>
      <div className="mt-4 flex flex-wrap items-center justify-center gap-2.5 max-w-2xl">
        {OLD_WAY.map((item, i) => {
          const struck = progress > 0.08 + i * 0.11;
          return (
            <span key={item}
              className="rounded-full border px-3.5 py-1.5 text-[11px] sm:text-sm font-medium"
              style={{ transition: "opacity 600ms ease-out, color 600ms, border-color 600ms",
                       borderColor: struck ? "rgba(255,255,255,0.08)" : "rgba(255,255,255,0.2)",
                       color: struck ? "rgba(244,246,253,0.3)" : "rgba(244,246,253,0.82)",
                       textDecoration: struck ? "line-through" : "none",
                       opacity: struck ? 0.55 : 1 }}>
              {item}
            </span>
          );
        })}
      </div>
      <div className="mt-9 h-px w-40 sm:w-64 gold-rule transition-opacity duration-1000"
           style={{ opacity: progress > 0.5 ? 1 : 0 }} />
      <h2 className="mt-7 max-w-3xl font-display text-2xl sm:text-4xl lg:text-5xl font-bold
                     leading-tight text-white transition-all duration-1000"
          style={{ opacity: progress > 0.52 ? 1 : 0,
                   transform: progress > 0.52 ? "none" : "translateY(14px)" }}>
        We didn't patch it. <span className="text-gold">We rebuilt it.</span>
      </h2>
      <p className="mt-5 text-[11px] sm:text-sm uppercase tracking-[0.28em] text-gold transition-opacity duration-1000"
         style={{ opacity: progress > 0.78 ? 1 : 0 }}>
        A new standard for property operations
      </p>
    </div>
  </div>
);

/* ================= The 2026 welcome film (v5): one continuous story, real product ================
   Six product moments, each caused by the one before it. Real captured interface is the hero; the
   photography is the world the operation happens in. No invented screens, numbers or customers —
   every frame of UI here is a screenshot of the running platform (scripts/capture_shots.py).   */

/** The reusable product moment: a real screen, a slow move across it, one idea over the top.
    Deliberately NOT a bullet list — the screen is the evidence, the line is the meaning. */
export const ScreenAct = ({ shot, kicker, line, focus = "center", move = "in", dim = 0.5,
                            align = "left", children, testid }) => (
  <div className="absolute inset-0 bg-royal-deep overflow-hidden" data-testid={`scene-${testid}`}>
    <Shot name={shot} move={move} dim={dim} focus={focus} />
    <div className="absolute inset-0"
         style={{ background: align === "left"
           ? "linear-gradient(90deg, rgba(9,15,40,0.985) 0%, rgba(9,15,40,0.965) 34%, rgba(9,15,40,0.78) 56%, rgba(9,15,40,0.12) 92%)"
           : "linear-gradient(0deg, rgba(9,15,40,0.97) 0%, rgba(9,15,40,0.6) 42%, rgba(9,15,40,0) 78%)" }} />
    <div className={`absolute inset-0 flex flex-col justify-center px-8 sm:px-14 lg:px-20
                     ${align === "left" ? "max-w-[64%] sm:max-w-[52%]" : "justify-end pb-24 sm:pb-28"}`}>
      <p className="tour-rise-1 text-gold text-[10px] sm:text-[11px] font-semibold uppercase tracking-[0.3em]">
        {kicker}
      </p>
      <h2 className="tour-rise-2 mt-3 max-w-2xl font-display text-xl sm:text-3xl lg:text-[2.6rem]
                     font-bold leading-tight text-white">
        {line}
      </h2>
      {children}
    </div>
  </div>
);

export const WorldScene = ({ progress = 0 }) => (
  <div className="absolute inset-0 bg-[#070c1e] overflow-hidden" data-testid="scene-world">
    <Photo name="coast_hero" move="in" dim={0.34} focus="50% 44%" />
    <div className="absolute inset-0 flex flex-col justify-end px-8 sm:px-14 lg:px-20 pb-24 sm:pb-28">
      <p className="tour-rise-1 text-gold text-[10px] sm:text-[11px] font-semibold uppercase tracking-[0.3em]">
        Property operations
      </p>
      <h2 className="tour-rise-2 mt-3 max-w-2xl font-display text-2xl sm:text-4xl lg:text-5xl
                     font-bold leading-tight text-white">
        Effortless from the outside
      </h2>
      <p className="mt-4 max-w-md text-sm sm:text-base text-blue-50/80 transition-opacity duration-1000"
         style={{ opacity: progress > 0.52 ? 1 : 0 }}>
        Behind it, a business is moving.
      </p>
    </div>
  </div>
);

/* The fragmented operation. Real screens, but each one alone in its own frame and drifting further
   apart — the friction is the distance between them, not the people. */
const FRAGMENTS = [
  { shot: "messages", label: "Messages", x: 4, y: 12, w: 30, d: -1.4 },
  { shot: "schedule", label: "Schedule", x: 38, y: 7, w: 28, d: 1.1 },
  { shot: "properties", label: "Property info", x: 69, y: 15, w: 28, d: -0.8 },
  { shot: "inspections", label: "Inspections", x: 8, y: 54, w: 28, d: 1.3 },
  { shot: "invoices", label: "Invoices", x: 40, y: 58, w: 27, d: -1.1 },
  { shot: "weather", label: "Conditions", x: 70, y: 52, w: 27, d: 0.9 },
];

export const FragmentScene = ({ progress = 0 }) => {
  const drift = progress * 2.6;                 // they pull apart as the narration lists them
  return (
    <div className="absolute inset-0 bg-[#070c1e] sidebar-grain overflow-hidden"
         data-testid="scene-fragments">
      {FRAGMENTS.map((f, i) => {
        const shown = progress >= i * 0.1;
        const away = (f.x < 40 ? -1 : f.x > 60 ? 1 : 0) * drift;
        return (
          <figure key={f.label} data-testid={`fragment-${f.shot}`}
                  className="absolute overflow-hidden rounded-lg border border-white/12
                             bg-royal-deep/70 shadow-[0_18px_50px_rgba(3,7,22,0.6)]"
                  style={{
                    // Position is static and ALL movement is transform-only: animating left/top/
                    // width re-laid out six panes on every update, which is what made this beat
                    // stutter. Transform and opacity stay on the compositor.
                    left: `${f.x}%`, top: `${f.y}%`, width: `${f.w}%`,
                    transform: `translate3d(${away * 3.2}%, ${(f.y > 40 ? drift : -drift) * 1.6}%, 0)`
                               + ` rotate(${f.d}deg) scale(${shown ? 1 : 0.94})`,
                    opacity: shown ? 0.92 - progress * 0.22 : 0,
                    willChange: "transform, opacity",
                    // Motion is a direct function of the clock. Driving it through a long
                    // transition meant every clock tick RESTARTED a 900ms ease, so the panes
                    // rubber-banded instead of drifting — that was the bad flow at 0:25. The
                    // short linear transition only smooths between clock ticks.
                    transition: "opacity 700ms ease-out, transform 90ms linear",
                  }}>
            <img src={`${THUMB}/${f.shot}.jpg?v=${SHOT_V}`} alt="" aria-hidden decoding="async"
                 className="h-24 w-full object-cover object-top sm:h-32"
                 style={{ filter: "saturate(0.85) brightness(0.92)" }} />
            <figcaption className="px-2.5 py-1.5 text-[9px] sm:text-[10px] font-semibold uppercase
                                   tracking-[0.18em] text-blue-100/60">
              {f.label}
            </figcaption>
          </figure>
        );
      })}
      <div className="absolute inset-0 flex items-center justify-center px-8">
        <div className="text-center transition-all duration-1000"
             style={{ opacity: progress > 0.66 ? 1 : 0,
                      transform: progress > 0.66 ? "none" : "translateY(12px)" }}>
          <p className="font-display text-xl sm:text-3xl lg:text-[2.6rem] font-bold text-white">
            The work was never the problem.
          </p>
          <p className="mt-2 font-display text-xl sm:text-3xl lg:text-[2.6rem] font-bold text-gold
                        transition-opacity duration-1000"
             style={{ opacity: progress > 0.82 ? 1 : 0 }}>
            The disconnected operation was.
          </p>
        </div>
      </div>
      <div className="absolute inset-0 pointer-events-none"
           style={{ background: "radial-gradient(130% 95% at 50% 50%, rgba(7,12,30,0.08) 30%, rgba(7,12,30,0.62) 100%)" }} />
    </div>
  );
};

/** The turn: the same frames stop drifting and draw into one place. No holograms — just distance
    closing. */
export const TurnScene = ({ progress = 0 }) => (
  <div className="absolute inset-0 bg-[#070c1e] sidebar-grain overflow-hidden" data-testid="scene-turn">
    {FRAGMENTS.map((f, i) => {
      // They travel most of the way in and STACK, rather than vanishing into a point — the beat
      // has to stay visually alive for its full six seconds.
      const pull = Math.min(1, progress * 1.6);
      const stagger = Math.max(0, Math.min(1, (progress - i * 0.04) * 1.6));
      const eased = 1 - Math.pow(1 - stagger, 3);
      // The travel is expressed as a transform so the six panes glide on the compositor instead of
      // forcing a full layout pass per frame.
      const dx = ((36 - f.x) / f.w) * 100 * eased;
      const dy = ((16 - f.y) / 26) * 100 * eased;
      return (
        <div key={f.label}
             className="absolute overflow-hidden rounded-lg border border-gold/30
                        shadow-[0_18px_50px_rgba(3,7,22,0.55)]"
             style={{
               left: `${f.x}%`, top: `${f.y}%`, width: `${f.w}%`,
               transform: `translate3d(${dx}%, ${dy}%, 0) rotate(${f.d * (1 - eased)}deg)`
                          + ` scale(${1 - eased * 0.18})`,
               opacity: 0.9 - eased * 0.35,
               zIndex: i,
               willChange: "transform, opacity",
               transition: "transform 90ms linear, opacity 500ms ease-out",
             }}>
          <img src={`${THUMB}/${f.shot}.jpg?v=${SHOT_V}`} alt="" aria-hidden decoding="async"
               className="h-20 w-full object-cover object-top sm:h-28"
               style={{ filter: `saturate(0.8) brightness(${0.9 - eased * 0.45})` }} />
        </div>
      );
    })}
    <div className="absolute inset-0"
         style={{ background: "radial-gradient(120% 85% at 50% 46%, rgba(7,12,30,0.35) 20%, rgba(7,12,30,0.82) 100%)" }} />
    <div className="absolute inset-x-0 bottom-0 top-1/2 flex flex-col items-center justify-center px-8">
      <div className="h-px gold-rule transition-all duration-1000"
           style={{ width: progress > 0.18 ? "min(72%, 36rem)" : 0,
                    opacity: progress > 0.18 ? 1 : 0 }} />
      <p className="mt-6 max-w-2xl text-center font-display text-lg sm:text-3xl lg:text-[2.4rem]
                    font-bold leading-tight text-white transition-all duration-1000"
         style={{ opacity: progress > 0.34 ? 1 : 0,
                  transform: progress > 0.34 ? "none" : "translateY(12px)" }}>
        This is not another tool to keep up with.
      </p>
    </div>
  </div>
);

export const RevealFilmScene = ({ progress = 0 }) => (
  <div className="absolute inset-0 bg-royal-deep overflow-hidden" data-testid="scene-revealfilm">
    <div className="absolute inset-0 transition-opacity duration-[1800ms]"
         style={{ opacity: Math.min(0.2 + progress * 1.6, 1) }}>
      <Shot name="dashboard" move="in" dim={0.62} />
    </div>
    {/* The brand block sits over a live screen, so it gets its own darkness to sit in — without
        this the dashboard's own type collided with the wordmark and neither could be read. */}
    <div className="absolute inset-0"
         style={{ background: "radial-gradient(75% 95% at 50% 50%, rgba(7,12,30,0.96) 0%, rgba(7,12,30,0.9) 45%, rgba(7,12,30,0.55) 100%)" }} />
    <div className="absolute inset-0 flex flex-col items-center justify-center px-6 text-center">
      <div className="tour-rise-1">
        <BrandBadge size="h-14 w-14 sm:h-20 sm:w-20" icon="h-10 w-10 sm:h-14 sm:w-14" />
      </div>
      <h2 className="tour-rise-2 mt-5 font-display text-3xl sm:text-5xl lg:text-6xl font-bold text-white">
        Cornerstone <span className="text-gold">Ops</span>
      </h2>
      <div className="tour-rise-3 mt-5 h-px w-40 sm:w-72 gold-rule" />
      <p className="tour-rise-3 mt-4 text-[10px] sm:text-sm uppercase tracking-[0.3em] text-blue-100/85">
        The Operational Command Center
      </p>
      {/* The claim the film is willing to make, because the product backs it up. */}
      <p className="mt-7 max-w-xl font-display text-base sm:text-xl lg:text-2xl font-bold
                    leading-snug text-white transition-all duration-1000"
         style={{ opacity: progress > 0.52 ? 1 : 0,
                  transform: progress > 0.52 ? "none" : "translateY(12px)" }}>
        Not a place to record what happened.
        <span className="block text-gold">A system that moves what happens next.</span>
      </p>
    </div>
  </div>
);

export const CommandScene = ({ progress = 0 }) => (
  <ScreenAct testid="command" shot="dashboard" move="in" dim={0.38} align="bottom"
             kicker="One view of the operation"
             line="Everything the business is doing, in one place">
    <div className="mt-5 flex flex-wrap gap-2">
      {["Properties", "People", "Work", "Money", "Conditions"].map((t, i) => (
        <span key={t} className="rounded-full border border-gold/35 bg-gold/10 px-3.5 py-1.5
                                 text-[10px] sm:text-xs font-semibold text-gold transition-all duration-700"
              style={{ opacity: progress > 0.28 + i * 0.1 ? 1 : 0,
                       transform: progress > 0.28 + i * 0.1 ? "none" : "translateY(8px)" }}>
          {t}
        </span>
      ))}
    </div>
  </ScreenAct>
);

export const ScheduleActScene = ({ progress = 0 }) => (
  <ScreenAct testid="scheduleact" shot="schedule" move="drift" dim={0.46}
             kicker="Turnovers · inspections · repairs"
             line="All of the work, on one schedule">
    <p className="mt-5 max-w-sm text-sm sm:text-base text-blue-50/85 transition-opacity duration-1000"
       style={{ opacity: progress > 0.45 ? 1 : 0 }}>
      The crew knows where to be. The office knows what is done.
    </p>
  </ScreenAct>
);

export const ConditionsScene = ({ progress = 0 }) => (
  <div className="absolute inset-0 bg-[#070c1e] overflow-hidden" data-testid="scene-conditions">
    <Photo name="storm" move="in" dim={0.46} focus="50% 42%" />
    <div className="absolute inset-0 flex items-center justify-center px-8">
      <p className="font-display text-2xl sm:text-4xl lg:text-5xl font-bold text-white text-center
                    transition-all duration-1000"
         style={{ opacity: progress > 0.18 ? 1 : 0,
                  transform: progress > 0.18 ? "none" : "translateY(14px)" }}>
        Then the conditions change.
      </p>
    </div>
  </div>
);

export const WeatherActScene = ({ progress = 0 }) => (
  <div className="absolute inset-0 bg-royal-deep overflow-hidden" data-testid="scene-weatheract">
    <Shot name="weather" move="in" dim={0.42} />
    {/* Full-width scrim: at 1920px+ the right half of the screenshot was bright enough to read as
        live UI competing with the line, instead of the environment behind it. */}
    <div className="absolute inset-0"
         style={{ background: "linear-gradient(90deg, rgba(9,15,40,0.985) 0%, rgba(9,15,40,0.96) 36%, rgba(9,15,40,0.9) 62%, rgba(9,15,40,0.88) 100%)" }} />
    <div className="absolute inset-0"
         style={{ background: "radial-gradient(120% 90% at 28% 50%, transparent 24%, rgba(7,12,30,0.7) 100%)" }} />
    <div className="absolute inset-0 flex flex-col justify-center px-8 sm:px-14 lg:px-20
                    max-w-[64%] sm:max-w-[52%]">
      <p className="tour-rise-1 text-gold text-[10px] sm:text-[11px] font-semibold uppercase tracking-[0.3em]">
        Weather intelligence · per property
      </p>
      <h2 className="tour-rise-2 mt-3 max-w-2xl font-display text-xl sm:text-3xl lg:text-[2.6rem]
                     font-bold leading-tight text-white">
        The official forecast for this property's own location
      </h2>
      <div className="mt-5 space-y-2">
        {["National Weather Service alerts", "National Hurricane Center tracking"].map((t, i) => (
          <div key={t} className="flex items-center gap-2.5 transition-all duration-700"
               style={{ opacity: progress > 0.3 + i * 0.22 ? 1 : 0,
                        transform: progress > 0.3 + i * 0.22 ? "none" : "translateX(-12px)" }}>
            <span className="h-1.5 w-1.5 rounded-full bg-gold shrink-0" />
            <span className="text-sm sm:text-base font-medium text-blue-50">{t}</span>
          </div>
        ))}
      </div>
    </div>
  </div>
);

/** The real briefing card, held as the hero. This is the moment the film is built around: weather
    stops being information and becomes work that is already in front of you. */
export const BriefingActScene = ({ progress = 0 }) => (
  <div className="absolute inset-0 bg-[#070c1e] overflow-hidden" data-testid="scene-briefingact">
    <div className="absolute inset-0"><Photo name="storm" move="drift" dim={0.5} focus="50% 40%" /></div>
    {/* The real briefing card is a tall panel, so it is held as a framed crop of its top — the
        sections and the official attribution are what the viewer needs to read, not all 900px. */}
    <div className="absolute inset-0 flex items-center justify-center px-6 pt-14 pb-28 sm:pt-16 sm:pb-32">
      <figure className="relative w-full max-w-xl overflow-hidden rounded-xl border border-gold/40
                         shadow-[0_34px_90px_rgba(3,7,22,0.85)] tour-rise-1"
              style={{ maxHeight: "100%" }}>
        <img src={`${SHOT}/briefing.jpg?v=${SHOT_V}`} alt="Property weather briefing"
             className="w-full tour-ken tour-ken-in"
             style={{ maxHeight: "56vh", objectFit: "cover", objectPosition: "top" }} />
        <div className="absolute inset-x-0 bottom-0 h-16"
             style={{ background: "linear-gradient(0deg, rgba(7,12,30,0.95), transparent)" }} />
      </figure>
    </div>
    <div className="absolute inset-x-0 top-5 sm:top-7 flex justify-center px-6">
      <p className="rounded-full border border-gold/40 bg-royal-deep/85 px-4 py-1.5 text-[9px]
                    sm:text-[11px] font-semibold uppercase tracking-[0.24em] text-gold">
        Written from the official data · sent to the owner in one click
      </p>
    </div>
    <div className="absolute inset-x-0 bottom-20 sm:bottom-24 flex justify-center px-8">
      <p className="max-w-2xl text-center font-display text-sm sm:text-xl lg:text-2xl font-bold text-white
                    transition-all duration-1000"
         style={{ opacity: progress > 0.6 ? 1 : 0,
                  transform: progress > 0.6 ? "none" : "translateY(10px)" }}>
        Changing conditions create work. Here, that work is already in front of you.
      </p>
    </div>
  </div>
);

export const AutomationActScene = ({ progress = 0 }) => (
  <div className="absolute inset-0 bg-royal-deep overflow-hidden" data-testid="scene-automationact">
    <Shot name="automations" move="in" dim={0.5} />
    <div className="absolute inset-0"
         style={{ background: "linear-gradient(90deg, rgba(9,15,40,0.25) 0%, rgba(9,15,40,0.86) 52%, rgba(9,15,40,0.98) 100%)" }} />
    <div className="absolute inset-y-0 right-0 flex w-full flex-col justify-center gap-2.5 px-8
                    sm:w-[52%] sm:px-12 pb-16">
      <p className="text-gold text-[10px] sm:text-[11px] font-semibold uppercase tracking-[0.3em]">
        Automations · you set the rules
      </p>
      {[["A guest checks out on Airbnb or VRBO", "The turnover clean is scheduled"],
        ["A turnover is completed", "The owner report goes out"],
        ["An issue is marked urgent", "The manager is notified on their phone"],
        ["A work order is closed", "The invoice is drafted"]].map(([when, then], i) => (
        <div key={when} data-testid={`rule-${i}`}
             className="rounded-lg border border-gold/25 bg-royal-deep/85 p-3
                        transition-all duration-700"
             style={{ opacity: progress > 0.16 + i * 0.16 ? 1 : 0,
                      transform: progress > 0.16 + i * 0.16 ? "none" : "translateX(16px)" }}>
          <p className="text-[9px] uppercase tracking-[0.18em] text-blue-100/55 font-semibold">When</p>
          <p className="text-xs sm:text-sm font-semibold text-white">{when}</p>
          <div className="my-1 flex items-center gap-1.5 text-gold">
            <ArrowRight className="h-3.5 w-3.5" />
            <span className="text-[9px] uppercase tracking-[0.18em] font-semibold">Then</span>
          </div>
          <p className="text-xs sm:text-sm text-blue-50/90">{then}</p>
        </div>
      ))}
      <p className="pt-1 text-[11px] sm:text-sm font-semibold text-gold transition-opacity duration-1000"
         style={{ opacity: progress > 0.72 ? 1 : 0 }}>
        The system moves it forward. You stay in control.
      </p>
    </div>
  </div>
);

export const FieldActScene = ({ progress = 0 }) => (
  <ScreenAct testid="fieldact" shot="workorder" move="in" dim={0.44}
             kicker="Issue · work order · record"
             line="A problem found becomes work that is tracked">
    <div className="mt-5 flex flex-wrap gap-2">
      {["Assigned", "Documented", "Closed", "On the record"].map((t, i) => (
        <span key={t} className="inline-flex items-center gap-1.5 rounded-full border border-gold/35
                                 bg-gold/10 px-3 py-1.5 text-[10px] sm:text-xs font-semibold text-gold
                                 transition-all duration-700"
              style={{ opacity: progress > 0.3 + i * 0.13 ? 1 : 0,
                       transform: progress > 0.3 + i * 0.13 ? "none" : "translateY(8px)" }}>
          <Check className="h-3 w-3" /> {t}
        </span>
      ))}
    </div>
  </ScreenAct>
);

export const OwnerPushScene = ({ progress = 0 }) => (
  <div className="absolute inset-0 bg-royal-deep overflow-hidden" data-testid="scene-ownerpush">
    <Shot name="reports" move="drift" dim={0.5} />
    <div className="absolute inset-0"
         style={{ background: "linear-gradient(90deg, rgba(9,15,40,0.985) 0%, rgba(9,15,40,0.94) 38%, rgba(9,15,40,0.55) 62%, rgba(9,15,40,0.2) 100%)" }} />
    <div className="absolute inset-0 flex flex-col justify-center px-8 sm:px-14 lg:px-20 max-w-[58%]">
      <p className="tour-rise-1 text-gold text-[10px] sm:text-[11px] font-semibold uppercase tracking-[0.3em]">
        Owner reporting · notifications
      </p>
      <h2 className="tour-rise-2 mt-3 max-w-xl font-display text-xl sm:text-3xl lg:text-[2.5rem]
                     font-bold leading-tight text-white">
        Owners see a company in control
      </h2>
      <p className="mt-4 max-w-md text-sm sm:text-base text-blue-50/85 transition-opacity duration-1000"
         style={{ opacity: progress > 0.22 ? 1 : 0 }}>
        They sign in to their own portal and see the property for themselves.
      </p>
    </div>
    <div className="absolute right-4 sm:right-10 top-1/2 -translate-y-1/2 w-[15rem] sm:w-[20rem] space-y-2.5">
      {[["Owner briefing sent", "Condition report emailed for Harbor Point Cottage."],
        ["Urgent issue raised", "Manager notified — water heater at Sandpiper Cottage."]]
        .map(([title, body], i) => (
        <div key={title} data-testid={`notice-${i}`}
             className="rounded-2xl border border-white/15 bg-white/[0.09] p-3.5"
             style={{ transition: "opacity 600ms ease-out, transform 600ms ease-out",
                      opacity: progress > 0.3 + i * 0.24 ? 1 : 0,
                      transform: progress > 0.3 + i * 0.24 ? "none" : "translateY(14px) scale(0.97)" }}>
          <p className="text-[9px] uppercase tracking-[0.2em] text-gold font-semibold">Cornerstone Ops</p>
          <p className="mt-1 text-xs sm:text-sm font-semibold text-white">{title}</p>
          <p className="text-[11px] sm:text-xs text-blue-50/85">{body}</p>
        </div>
      ))}
    </div>
  </div>
);

/** The booking channels: a checkout on Airbnb or VRBO lands on the schedule as a turnover.
    The channel cards are our own marks — no platform logos — over the real Work Orders screen. */
export const ChannelActScene = ({ progress = 0 }) => (
  <div className="absolute inset-0 bg-royal-deep overflow-hidden" data-testid="scene-channelact">
    <Shot name="schedule" move="drift" dim={0.5} />
    <div className="absolute inset-0"
         style={{ background: "linear-gradient(90deg, rgba(9,15,40,0.985) 0%, rgba(9,15,40,0.95) 40%, rgba(9,15,40,0.7) 66%, rgba(9,15,40,0.3) 100%)" }} />
    <div className="absolute inset-0 flex flex-col justify-center px-8 sm:px-14 lg:px-20 max-w-[62%] sm:max-w-[54%]">
      <p className="tour-rise-1 text-gold text-[10px] sm:text-[11px] font-semibold uppercase tracking-[0.3em]">
        Airbnb · VRBO · calendar sync
      </p>
      <h2 className="tour-rise-2 mt-3 max-w-xl font-display text-xl sm:text-3xl lg:text-[2.5rem]
                     font-bold leading-tight text-white">
        A guest checks out. The turnover is already scheduled.
      </h2>
      <div className="mt-6 flex flex-wrap items-center gap-2.5">
        {[["Airbnb", CalendarDays], ["VRBO", CalendarDays]].map(([name, Icon], i) => (
          <span key={name} data-testid={`channel-${name.toLowerCase()}`}
                className="inline-flex items-center gap-2 rounded-lg border border-white/18
                           bg-white/[0.07] px-3.5 py-2 text-xs sm:text-sm font-semibold text-blue-50"
                style={{ transition: "opacity 600ms ease-out, transform 600ms ease-out",
                         opacity: progress > 0.14 + i * 0.1 ? 1 : 0,
                         transform: progress > 0.14 + i * 0.1 ? "none" : "translateY(10px)" }}>
            <Icon className="h-4 w-4 text-gold" /> {name} calendar
          </span>
        ))}
        <ArrowRight className="h-5 w-5 text-gold transition-opacity duration-700"
                    style={{ opacity: progress > 0.4 ? 1 : 0 }} />
        <span data-testid="channel-result"
              className="inline-flex items-center gap-2 rounded-lg border border-gold/45 bg-gold/12
                         px-3.5 py-2 text-xs sm:text-sm font-semibold text-gold"
              style={{ transition: "opacity 700ms ease-out, transform 700ms ease-out",
                       opacity: progress > 0.48 ? 1 : 0,
                       transform: progress > 0.48 ? "none" : "translateX(-10px)" }}>
          <Check className="h-4 w-4" /> Turnover clean scheduled
        </span>
      </div>
      <p className="mt-6 max-w-md text-sm sm:text-base text-blue-50/85 transition-opacity duration-1000"
         style={{ opacity: progress > 0.7 ? 1 : 0 }}>
        Nobody has to book it. Nobody has to remember it.
      </p>
    </div>
  </div>
);

/** Dispatch: the push notification lands on the crew's phone, and one tap starts navigation.
    The map is our own stylised card, not a third-party map surface. */
export const DispatchActScene = ({ progress = 0 }) => (
  <div className="absolute inset-0 bg-royal-deep overflow-hidden" data-testid="scene-dispatchact">
    <Shot name="workorder" move="in" dim={0.55} />
    <div className="absolute inset-0"
         style={{ background: "linear-gradient(90deg, rgba(9,15,40,0.98) 0%, rgba(9,15,40,0.9) 42%, rgba(9,15,40,0.72) 100%)" }} />
    <div className="absolute inset-0 flex flex-col justify-center px-8 sm:px-14 lg:px-20 max-w-[52%]">
      <p className="tour-rise-1 text-gold text-[10px] sm:text-[11px] font-semibold uppercase tracking-[0.3em]">
        Push notifications · directions
      </p>
      <h2 className="tour-rise-2 mt-3 max-w-lg font-display text-xl sm:text-3xl lg:text-[2.4rem]
                     font-bold leading-tight text-white">
        Assigned, and the crew is already on the way
      </h2>
    </div>
    {/* the phone */}
    <div className="absolute right-6 sm:right-16 top-1/2 -translate-y-1/2 w-[15rem] sm:w-[19rem]">
      <div className="rounded-[1.6rem] border border-white/15 bg-[#0b1330]/95 p-3
                      shadow-[0_30px_80px_rgba(3,7,22,0.8)]"
           style={{ transition: "opacity 700ms ease-out, transform 700ms ease-out",
                    opacity: progress > 0.1 ? 1 : 0,
                    transform: progress > 0.1 ? "none" : "translateY(18px) scale(0.97)" }}>
        <div data-testid="dispatch-push"
             className="rounded-xl border border-white/15 bg-white/[0.09] p-3.5">
          <p className="text-[9px] uppercase tracking-[0.2em] text-gold font-semibold">Cornerstone Ops</p>
          <p className="mt-1 text-xs sm:text-sm font-semibold text-white">New work order assigned</p>
          <p className="text-[11px] sm:text-xs text-blue-50/85">
            Turnover clean · Harbor Point Cottage · today 11:00 AM
          </p>
        </div>
        {/* the stylised route card */}
        <div data-testid="dispatch-map"
             className="mt-2.5 overflow-hidden rounded-xl border border-white/12 bg-[#101a3d]"
             style={{ transition: "opacity 700ms ease-out, transform 700ms ease-out",
                      opacity: progress > 0.46 ? 1 : 0,
                      transform: progress > 0.46 ? "none" : "translateY(12px)" }}>
          <div className="relative h-28 sm:h-32"
               style={{ backgroundImage:
                 "linear-gradient(rgba(255,255,255,0.05) 1px, transparent 1px),"
                 + "linear-gradient(90deg, rgba(255,255,255,0.05) 1px, transparent 1px)",
                 backgroundSize: "22px 22px" }}>
            <svg viewBox="0 0 220 120" className="absolute inset-0 h-full w-full">
              <path d="M24 100 C 70 100, 70 54, 118 54 S 176 32, 196 24"
                    fill="none" stroke="rgba(212,175,55,0.9)" strokeWidth="3"
                    strokeLinecap="round" strokeDasharray="260"
                    strokeDashoffset={260 - 260 * Math.min(1, Math.max(0, (progress - 0.5) * 3.2))}
                    style={{ transition: "stroke-dashoffset 120ms linear" }} />
              <circle cx="24" cy="100" r="5" fill="#f4f6fd" />
              <circle cx="196" cy="24" r="6" fill="#D4AF37" />
            </svg>
            <span className="absolute left-2.5 bottom-2 inline-flex items-center gap-1.5 rounded-full
                             bg-royal-deep/85 px-2.5 py-1 text-[9px] font-semibold uppercase
                             tracking-[0.16em] text-blue-50">
              <Navigation className="h-3 w-3 text-gold" /> Directions
            </span>
            <span className="absolute right-2.5 top-2 inline-flex items-center gap-1.5 rounded-full
                             bg-gold/15 px-2.5 py-1 text-[9px] font-semibold text-gold">
              <MapPin className="h-3 w-3" /> 412 Harbor
            </span>
          </div>
        </div>
      </div>
      <p className="mt-3 text-center text-[11px] sm:text-xs text-blue-100/70 transition-opacity duration-1000"
         style={{ opacity: progress > 0.72 ? 1 : 0 }}>
        One tap opens directions to the door.
      </p>
    </div>
  </div>
);

/** The rest of the platform, shown as real screens rather than claimed in a list. */
const DEPTH = [
  { shot: "inspections", label: "Inspections" },
  { shot: "issues", label: "Issues" },
  { shot: "vendors", label: "Vendors" },
  { shot: "team", label: "Crews & team" },
];

export const DepthActScene = ({ progress = 0 }) => (
  <div className="absolute inset-0 bg-[#070c1e] sidebar-grain overflow-hidden"
       data-testid="scene-depthact">
    <div className="absolute inset-0 opacity-30"><Photo name="coast_dune" move="drift" dim={0.66} /></div>
    <div className="absolute inset-0 flex flex-col justify-center px-8 sm:px-14 lg:px-20">
      <p className="tour-rise-1 text-gold text-[10px] sm:text-[11px] font-semibold uppercase tracking-[0.3em]">
        One system · the whole operation
      </p>
      <h2 className="tour-rise-2 mt-3 max-w-2xl font-display text-xl sm:text-3xl lg:text-[2.5rem]
                     font-bold leading-tight text-white">
        Inspections, issues, vendors and crews — all in here
      </h2>
      <div className="mt-7 grid grid-cols-2 gap-3 sm:grid-cols-4 sm:gap-4 max-w-4xl">
        {DEPTH.map((d, i) => (
          <figure key={d.shot} data-testid={`depth-${d.shot}`}
                  className="overflow-hidden rounded-lg border border-white/12 bg-royal-deep/70
                             shadow-[0_18px_50px_rgba(3,7,22,0.6)]"
                  style={{ transition: "opacity 700ms ease-out, transform 700ms ease-out",
                           opacity: progress > 0.16 + i * 0.12 ? 1 : 0,
                           transform: progress > 0.16 + i * 0.12 ? "none" : "translateY(16px) scale(0.97)" }}>
            <img src={`${THUMB}/${d.shot}.jpg?v=${SHOT_V}`} alt="" aria-hidden decoding="async"
                 className="h-20 w-full object-cover object-top sm:h-28"
                 style={{ filter: "saturate(0.88) brightness(0.95)" }} />
            <figcaption className="px-2.5 py-1.5 text-[9px] sm:text-[10px] font-semibold uppercase
                                   tracking-[0.18em] text-blue-100/70">
              {d.label}
            </figcaption>
          </figure>
        ))}
      </div>
      <p className="mt-6 max-w-lg text-sm sm:text-base text-blue-50/85 transition-opacity duration-1000"
         style={{ opacity: progress > 0.7 ? 1 : 0 }}>
        Every property carries a history you can actually read.
      </p>
    </div>
  </div>
);

/** The money: completed work becomes an invoice, and the owner pays it online. */
export const MoneyActScene = ({ progress = 0 }) => (
  <div className="absolute inset-0 bg-royal-deep overflow-hidden" data-testid="scene-moneyact">
    <Shot name="invoices" move="in" dim={0.5} />
    <div className="absolute inset-0"
         style={{ background: "linear-gradient(90deg, rgba(9,15,40,0.35) 0%, rgba(9,15,40,0.88) 50%, rgba(9,15,40,0.98) 100%)" }} />
    <div className="absolute inset-y-0 right-0 flex w-full flex-col justify-center gap-2.5 px-8
                    sm:w-[54%] sm:px-12">
      <p className="text-gold text-[10px] sm:text-[11px] font-semibold uppercase tracking-[0.3em]">
        Invoicing · online payments
      </p>
      <h2 className="mt-1 max-w-lg font-display text-xl sm:text-3xl lg:text-[2.3rem] font-bold
                     leading-tight text-white">
        The work gets billed, and the money comes in
      </h2>
      <div className="mt-4 space-y-2">
        {[[Check, "Work completed", "Turnover clean closed out"],
          [Receipt, "Invoice drafted", "Automatically, or on your command"],
          [CreditCard, "Paid online", "Card or bank, straight from the invoice"],
          [Zap, "Posted", "Balance updated without anyone typing it in"]].map(
          ([Icon, title, body], i) => (
          <div key={title} data-testid={`money-step-${i}`}
               className="flex items-start gap-3 rounded-lg border border-gold/25 bg-royal-deep/85 p-3"
               style={{ transition: "opacity 650ms ease-out, transform 650ms ease-out",
                        opacity: progress > 0.14 + i * 0.14 ? 1 : 0,
                        transform: progress > 0.14 + i * 0.14 ? "none" : "translateX(16px)" }}>
            <Icon className="mt-0.5 h-4 w-4 shrink-0 text-gold" />
            <div>
              <p className="text-xs sm:text-sm font-semibold text-white">{title}</p>
              <p className="text-[11px] sm:text-xs text-blue-50/85">{body}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  </div>
);

/** The close.
 *
 *  Each written line appears when THAT line is actually spoken. It used to be staged on scene
 *  progress (0.12 / 0.3 / 0.48 of the way through the beat), which had no relationship to the
 *  read, so the words on screen drifted away from the words in the speakers. Now the cue sheet
 *  drives it: a line shows the moment its own clip starts playing.
 */
export const CloseFilmScene = ({ progress = 0, onStart, time = 0, cues = [] }) => {
  const spoken = (text) => {
    const cue = cues.find((c) => c.text === text);
    return cue ? time >= cue.start - 0.12 : progress > 0.12;
  };
  return (
  <div className="absolute inset-0 bg-royal-deep sidebar-grain overflow-hidden" data-testid="scene-closefilm">
    <div className="absolute inset-0 opacity-30"><Photo name="coast_dune" move="drift" dim={0.7} /></div>
    <div className="relative h-full flex flex-col items-center justify-center px-6 text-center pb-32">
      <div className="tour-rise-1">
        <BrandBadge size="h-12 w-12 sm:h-16 sm:w-16" icon="h-9 w-9 sm:h-11 sm:w-11" />
      </div>
      <h2 className="tour-rise-2 mt-4 font-display text-2xl sm:text-4xl lg:text-5xl font-bold text-white">
        Cornerstone <span className="text-gold">Ops</span>
      </h2>
      <div className="tour-rise-3 mt-4 h-px w-40 sm:w-72 gold-rule" />
      <p className="tour-rise-3 mt-4 text-[10px] sm:text-sm uppercase tracking-[0.3em] text-blue-100/85">
        The Operational Command Center
      </p>
      <div className="mt-8 space-y-1.5">
        {["This is what a connected operation looks like."].map((line) => (
          <p key={line} data-testid="close-line-0"
             className="mx-auto max-w-xl font-display text-sm sm:text-lg text-blue-50/90
                        transition-opacity duration-700"
             style={{ opacity: spoken(line) ? 1 : 0 }}>
            {line}
          </p>
        ))}
        {["Connect the operation.", "Move it forward."].map((line, i) => (
          <p key={line} data-testid={`close-line-${i + 1}`}
             className="font-display text-lg sm:text-2xl lg:text-3xl font-bold text-white"
             style={{ transition: "opacity 600ms ease-out, transform 600ms ease-out",
                      opacity: spoken(line) ? 1 : 0,
                      transform: spoken(line) ? "none" : "translateY(10px)" }}>
            {line}
          </p>
        ))}
      </div>
      <button data-testid="tour-cta-start" onClick={onStart}
        className="mt-8 h-11 rounded-full bg-gold px-8 font-display text-xs sm:text-sm font-bold
                   uppercase tracking-[0.16em] text-royal-deep transition-transform duration-200
                   hover:scale-[1.03]"
        style={{ opacity: spoken("Move it forward.") ? 1 : 0,
                 transition: "opacity 800ms ease-out" }}>
        Start Free Trial
      </button>
      {/* The scripture the company is built on — the last thing on screen, as on the site. */}
      <div className="absolute inset-x-0 bottom-16 sm:bottom-20 flex flex-col items-center px-6
                      text-center transition-opacity duration-1000"
           data-testid="close-scripture"
           style={{ opacity: progress > 0.5 ? 1 : 0 }}>
        <p className="text-[9px] sm:text-[10px] font-semibold uppercase tracking-[0.3em] text-gold">
          Psalm 24:1, NLT
        </p>
        <p className="mt-1.5 max-w-xl text-[11px] sm:text-sm italic text-blue-50/80">
          “The earth is the LORD’S, and everything in it.”
        </p>
        <p className="mt-2 text-[9px] sm:text-[10px] font-semibold uppercase tracking-[0.26em]
                      text-blue-100/70">
          God owns it. &nbsp;·&nbsp; We steward it. &nbsp;·&nbsp; We protect it.
        </p>
        {/* CC BY 4.0 attribution for the score — required by the licence. */}
        <p className="mt-3 text-[8px] sm:text-[9px] tracking-[0.12em] text-blue-100/35">
          Music: “Inspired” by Kevin MacLeod · incompetech.com · CC BY 4.0
        </p>
      </div>
    </div>
  </div>
  );
};

export const SCENES = {
  /* the 2026 welcome film (v5) */
  world: WorldScene, fragments: FragmentScene, turn: TurnScene, revealfilm: RevealFilmScene,
  command: CommandScene, scheduleact: ScheduleActScene, conditions: ConditionsScene,
  channelact: ChannelActScene, dispatchact: DispatchActScene, depthact: DepthActScene,
  moneyact: MoneyActScene,
  weatheract: WeatherActScene, briefingact: BriefingActScene, automationact: AutomationActScene,
  fieldact: FieldActScene, ownerpush: OwnerPushScene, closefilm: CloseFilmScene,
  /* earlier film — kept so the previous cut still plays if it is ever restored */
  bridge: BridgeScene,
  coast: CoastScene, channels: ChannelScene, field: FieldScene, vendorflow: VendorFlowScene,
  weathertwo: WeatherTwoScene, briefing: BriefingScene, pushphone: PushPhoneScene,
  closing: ClosingScene,
  chaos: ChaosScene, question: QuestionScene, reveal: RevealScene, converge: ConvergeScene,
  workflow: WorkflowScene, automation: AutomationScene, visibility: VisibilityScene,
  scale: ScaleScene, human: HumanScene, statement: StatementScene, cta: CtaScene,
  statement2: StatementBridge,
  properties: moduleScene("properties"), schedule: moduleScene("schedule"),
  weather: moduleScene("weather"), push: moduleScene("push"),
  owner: moduleScene("owner"), directions: moduleScene("directions"),
  connected: ConnectedScene,
  team: moduleScene("team"), inspections: moduleScene("inspections"),
  issues: moduleScene("issues"), vendors: moduleScene("vendors"),
  invoices: moduleScene("invoices"), finance: moduleScene("finance"),
  messages: moduleScene("messages"), mobile: moduleScene("mobile"),
  trust: moduleScene("trust"), pricing: moduleScene("pricing"),
};
